import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Upload, FileSpreadsheet, AlertCircle, 
  Check, X, Download
} from 'lucide-react';

const ImportList = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || 
          file.type === 'application/vnd.ms-excel' ||
          file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        setFile(file);
        // Here you would normally parse the file and show a preview
        // For demo purposes, we'll just show some dummy data
        setPreview([
          { text: 'Location A - System Upgrade', status: 'Not Started' },
          { text: 'Location B - System Upgrade', status: 'Not Started' },
          { text: 'Location C - System Upgrade', status: 'Not Started' }
        ]);
        setError(null);
      } else {
        setError('Please upload a CSV or Excel file');
        setFile(null);
        setPreview(null);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      fileInputRef.current.files = e.dataTransfer.files;
      handleFileChange({ target: { files: [file] } });
    }
  };

  const preventDefault = (e) => {
    e.preventDefault();
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <button
            onClick={() => navigate('/tracker')}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Trackers
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Import List</h1>
        </div>
      </div>

      {/* Template Download */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <FileSpreadsheet className="w-5 h-5 text-blue-500 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-900">Download Template</h3>
            <p className="mt-1 text-sm text-blue-600">
              Use our template to ensure your data is formatted correctly for import.
            </p>
            <button className="mt-2 flex items-center text-sm font-medium text-blue-700 hover:text-blue-800">
              <Download className="w-4 h-4 mr-1" />
              Download Template
            </button>
          </div>
        </div>
      </div>

      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={preventDefault}
        onDragEnter={preventDefault}
        className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-blue-400 transition-colors"
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          accept=".csv,.xlsx,.xls"
          className="hidden"
        />
        <div className="space-y-4">
          <div className="w-12 h-12 rounded-full bg-blue-50 mx-auto flex items-center justify-center">
            <Upload className="w-6 h-6 text-blue-600" />
          </div>
          <div className="space-y-2">
            <h3 className="text-gray-900 font-medium">Upload your file</h3>
            <p className="text-sm text-gray-500">
              Drag and drop your CSV or Excel file, or click to browse
            </p>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 
              bg-blue-50 rounded-lg hover:bg-blue-100"
          >
            Choose File
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-100 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-900">Error</h3>
              <p className="mt-1 text-sm text-red-600">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
          <div className="space-y-3">
            {preview.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-sm text-gray-700">{item.text}</span>
                <span className="text-sm text-gray-500">{item.status}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => navigate('/tracker')}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // Handle import
                navigate('/tracker');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
            >
              <Check className="w-4 h-4 mr-2" />
              Import Items
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportList; 